import { useMemo, useRef, useState } from "react";
import Loader from "../icons/Loader";
import { User } from "@prisma/client";
import { UsersSearchResponse } from "@api/src/types/responses";
import { UserSearched } from "@api/src/types/user";
import { XMarkIcon } from "@heroicons/react/24/outline";

type UserSearchProps = {
  onSave: (user: UserSearched) => void;
  onClose: () => void;
  quotedUsers: Array<UserSearched>;
};

const UserSearch = ({ onSave, quotedUsers, onClose }: UserSearchProps) => {
  const [search, setSearch] = useState("");
  const [users, setUsers] = useState<UserSearched[]>([]);
  const [isLoadingUser, setIsLoadingUser] = useState(false);
  const userSearchFormRef = useRef<HTMLFormElement>(null);

  const quotedUsersIds = useMemo(() => {
    const quotedUsersIds: Record<User["id"], boolean> = {};
    quotedUsers.forEach((user) => {
      quotedUsersIds[user.id] = true;
    });
    return quotedUsersIds;
  }, [quotedUsers]);

  return (
    <form action="/actions/user-search" method="GET" className="relative h-10 flex-1" ref={userSearchFormRef}>
      <input
        className="w-full px-3 py-2 text-left text-sky-900"
        type="text"
        name="userSearch"
        autoComplete="off"
        autoFocus
        onKeyDown={(event) => {
          if (event.key === "Escape") {
            setSearch("");
            setUsers([]);
            setIsLoadingUser(false);
            onClose();
          }
        }}
        onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
          setIsLoadingUser(true);
          setSearch(event.currentTarget.value);
          fetch(`${import.meta.env.VITE_API_URL}/users/search?userSearch=${event.currentTarget.value}`, {
            method: "GET",
            credentials: "include",
          })
            .then((response) => response.json() as Promise<UsersSearchResponse>)
            .then((response) => {
              if (response.ok) {
                setUsers(response.data.users);
              }
            })
            .finally(() => {
              setIsLoadingUser(false);
            });
        }}
        placeholder="Tapez le nom d'un utilisateur..."
      />
      <button type="button" className="absolute right-0 my-auto bottom-0 top-0 -ml-1 mr-3" onClick={onClose}>
        <XMarkIcon className="h-4 w-4" />
      </button>
      {!!isLoadingUser && <Loader className="absolute right-0 top-2.5 -ml-1 mr-3 " />}
      {!!search && (
        <div className="absolute bottom-10 flex w-full flex-col rounded border border-gray-300 bg-white">
          {!users?.length ? (
            <span>Pas d'utilisateur</span>
          ) : (
            <>
              {users
                .filter((user) => !quotedUsersIds[user.id])
                .map((user) => (
                  <button
                    type="button"
                    className="w-full p-2 text-left"
                    key={user.id}
                    onClick={() => {
                      onSave(user);
                      userSearchFormRef.current?.reset();
                      setSearch("");
                      setUsers([]);
                      onClose();
                    }}
                  >
                    {user.firstName} {user.lastName}
                  </button>
                ))}
            </>
          )}
        </div>
      )}
    </form>
  );
};

export default UserSearch;
